import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { nanoid } from "nanoid";
import { useDrag, useDrop, DndProvider } from "react-dnd";
import QRCode from "qrcode";
import { HTML5Backend } from "react-dnd-html5-backend";
import {
  Card,
  Text,
  Stack,
  Button,
  Select,
  Modal,
  TextInput,
  Textarea,
  NumberInput,
  Switch,
  Divider,
  Box,
  ActionIcon,
  Alert,
  Code,
  CopyButton,
  Grid,
  Paper,
  Title,
  LoadingOverlay,
  Container,
  SimpleGrid,
  Group,
  ColorInput,
  Accordion,
  Slider,
  Tabs,
  Badge,
  Tooltip,
  Anchor,
  Center,
  Loader,
  ScrollArea,
  SegmentedControl,
  ThemeIcon,
} from "@mantine/core";
import {
  IconGripVertical,
  IconEdit,
  IconTrash,
  IconCheck,
  IconUser,
  IconMail,
  IconForms,
  IconPhone,
  IconLink,
  IconCalendar,
  IconNumbers,
  IconBuilding,
  IconTools,
  IconRefresh,
  IconDeviceMobile,
  IconPlus,
  IconPalette,
  IconTypography,
  IconAdjustmentsHorizontal,
  IconSparkles,
  IconCopy,
  IconDeviceDesktop,
  IconDownload,
  IconCode,
  IconExternalLink,
  IconTerminal2,
  IconWorld,
} from "@tabler/icons-react";
import {
  collection,
  addDoc,
  serverTimestamp,
  getDocs,
  query,
  where,
  updateDoc,
  doc,
  limit,
} from "firebase/firestore";
import { db } from "../../firebase/FirebaseCredential";
import { useAppSelector } from "../../hooks/hooks";
import { RootState } from "../../store/store";
import BenchSettingsBreadcrumbs from "../UIComponent/Components/BenchSettingsBreadcrumbs";
import BenchTopHeader from "../UIComponent/Components/BenchTopHeader";
import BenchHeading from "../pages/UI/BenchHeading";

// Types
type FieldType =
  | "text"
  | "email"
  | "textarea"
  | "select"
  | "number"
  | "tel"
  | "url"
  | "date";

interface Validation {
  required?: boolean;
  min_length?: number;
  max_length?: number;
  pattern?: string;
  custom_message?: string;
}

interface FormField {
  id: string;
  type: FieldType;
  label: string;
  placeholder?: string;
  options?: string[];
  validation?: Validation;
  isRequired?: boolean;
  isRemovable?: boolean;
  dynamicOptions?: {
    type: "branch" | "service";
    dependsOn?: string;
  };
}

interface FormStyles {
  primaryColor: string;
  primaryTextColor: string;
  backgroundColor: string;
  cardBackgroundColor: string;
  textColor: string;
  subtextColor: string;
  inputBgColor: string;
  borderColor: string;
  focusBorderColor: string;
  fontFamily: string;
  fontSize: number;
  labelWeight: number;
  borderRadius: number;
  inputPadding: number;
  borderWidth: number;
  boxShadow: "none" | "sm" | "md" | "lg" | "xl";
}

interface CustomForm {
  company_id: string;
  form_id: string;
  title: string;
  send_to: string;
  description: string;
  fields: FormField[];
  styles: FormStyles;
  created_by: string | null;
  created_at: any;
  is_active: boolean;
  allowed_domains: string[];
  form_type?: "dynamic" | "single_branch";
  branch_id?: string;
}

interface Branch {
  id: string;
  name: string;
  code: string;
  is_active: boolean;
}

interface Service {
  id: string;
  name: string;
  branch_id: string;
  branch_code: string;
  is_active: boolean;
}

interface DragItem {
  type: "field" | "existing-field";
  field?: FormField;
  fieldType?: FieldType;
  index?: number;
}

const COLOR_THEME_PRESETS: Record<string, Partial<FormStyles>> = {
  modern_crimson: {
    primaryColor: "#dc3545",
    primaryTextColor: "#ffffff",
    backgroundColor: "#ffffff",
    cardBackgroundColor: "#ffffff",
    textColor: "#1e293b",
    subtextColor: "#64748b",
    inputBgColor: "#ffffff",
    borderColor: "#e2e8f0",
    focusBorderColor: "#dc3545",
    borderRadius: 8,
    boxShadow: "sm",
  },
  deep_emerald: {
    primaryColor: "#059669",
    primaryTextColor: "#ffffff",
    backgroundColor: "#f0fdf4",
    cardBackgroundColor: "#ffffff",
    textColor: "#064e3b",
    subtextColor: "#047857",
    inputBgColor: "#ffffff",
    borderColor: "#a7f3d0",
    focusBorderColor: "#059669",
    borderRadius: 10,
    boxShadow: "md",
  },
  midnight_indigo: {
    primaryColor: "#4f46e5",
    primaryTextColor: "#ffffff",
    backgroundColor: "#f8fafc",
    cardBackgroundColor: "#ffffff",
    textColor: "#0f172a",
    subtextColor: "#475569",
    inputBgColor: "#ffffff",
    borderColor: "#cbd5e1",
    focusBorderColor: "#4f46e5",
    borderRadius: 12,
    boxShadow: "md",
  },
  clean_slate: {
    primaryColor: "#0f172a",
    primaryTextColor: "#ffffff",
    backgroundColor: "#f1f5f9",
    cardBackgroundColor: "#ffffff",
    textColor: "#0f172a",
    subtextColor: "#64748b",
    inputBgColor: "#ffffff",
    borderColor: "#cbd5e1",
    focusBorderColor: "#0f172a",
    borderRadius: 6,
    boxShadow: "sm",
  },
  minimal_dark: {
    primaryColor: "#3b82f6",
    primaryTextColor: "#ffffff",
    backgroundColor: "#0f172a",
    cardBackgroundColor: "#1e293b",
    textColor: "#f8fafc",
    subtextColor: "#94a3b8",
    inputBgColor: "#0f172a",
    borderColor: "#334155",
    focusBorderColor: "#3b82f6",
    borderRadius: 8,
    boxShadow: "lg",
  },
};

const OPTIONAL_FIELD_PRESETS: Record<string, FormField> = {
  email: {
    id: "email",
    type: "email",
    label: "Email Address",
    placeholder: "Enter your email address",
    validation: {
      required: false,
      pattern: "^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$",
      custom_message: "Please enter a valid email address",
    },
    isRequired: false,
    isRemovable: true,
  },
  service: {
    id: "service",
    type: "select",
    label: "Select Service",
    placeholder: "Choose a service",
    options: [],
    validation: { required: false },
    isRequired: false,
    isRemovable: true,
    dynamicOptions: {
      type: "service",
      dependsOn: "branch",
    },
  },
};

const fetchBranches = async (companyId: string): Promise<Branch[]> => {
  if (!companyId) return [];
  try {
    const branchesQuery = query(
      collection(db, "branch"),
      where("company_id", "==", companyId),
    );
    const querySnapshot = await getDocs(branchesQuery);
    const branches: Branch[] = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.is_active !== false) {
        branches.push({
          id: data.branch_id || doc.id,
          name: data.branch_name || data.name || "Unnamed Branch",
          code: data.branch_code || data.code || doc.id,
          is_active: data.is_active !== false,
        });
      }
    });

    return branches;
  } catch (error) {
    console.error("Error fetching branches:", error);
    return [];
  }
};

const fetchServices = async (
  branchId?: string,
  branchCode?: string,
): Promise<Service[]> => {
  try {
    let q;
    if (branchId) {
      q = query(collection(db, "services"), where("branch_id", "==", branchId));
    } else if (branchCode) {
      q = query(
        collection(db, "services"),
        where("branch_code", "==", branchCode),
      );
    } else {
      q = query(collection(db, "services"), where("is_active", "==", true));
    }

    const querySnapshot = await getDocs(q);
    const services: Service[] = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.is_active !== false) {
        services.push({
          id: doc.id,
          name: data.name || data.service_name || doc.id,
          branch_id: data.branch_id,
          branch_code: data.branch_code,
          is_active: data.is_active !== false,
        });
      }
    });

    return services;
  } catch (error) {
    console.error("Error fetching services:", error);
    return [];
  }
};

// Removed email and preferred communication so they appear in Available Optional Fields by default
const getDefaultFields = (): FormField[] => [
  {
    id: "full_name",
    type: "text",
    label: "Full Name",
    placeholder: "Enter your full name",
    validation: { required: true },
    isRequired: true,
    isRemovable: false,
  },
  {
    id: "branch",
    type: "select",
    label: "Select Branch",
    placeholder: "Choose your branch",
    options: [],
    validation: { required: true },
    isRequired: true,
    isRemovable: false,
    dynamicOptions: {
      type: "branch",
    },
  },

  {
    id: "mobile_number",
    type: "tel",
    label: "Mobile Number",
    placeholder: "Enter your mobile number",
    validation: {
      required: false,
      pattern: "^[0-9]{10}$",
      custom_message: "Please enter a valid 10-digit mobile number",
    },
    isRequired: false,
    isRemovable: false,
  },
];

// Field Card Component for Canvas
const FieldCard: React.FC<{
  field: FormField;
  index: number;
  onEdit: (field: FormField) => void;
  onRemove: (id: string) => void;
  moveField: (fromIndex: number, toIndex: number) => void;
}> = ({ field, index, onEdit, onRemove, moveField }) => {
  const [{ isDragging }, drag, preview] = useDrag({
    type: "existing-field",
    item: { type: "existing-field", field, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [{ isOver }, drop] = useDrop({
    accept: ["field", "existing-field"],
    drop: (item: DragItem) => {
      if (item.type === "existing-field" && item.index !== undefined) {
        moveField(item.index, index);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  const getFieldIcon = (type: FieldType) => {
    switch (type) {
      case "text":
        return IconUser;
      case "email":
        return IconMail;
      case "textarea":
        return IconForms;
      case "select":
        return field.id === "branch"
          ? IconBuilding
          : field.id === "service"
            ? IconTools
            : IconForms;
      case "number":
        return IconNumbers;
      case "tel":
        return IconPhone;
      case "url":
        return IconLink;
      case "date":
        return IconCalendar;
      default:
        return IconForms;
    }
  };

  const FieldIcon = getFieldIcon(field.type);

  return (
    <div ref={(node) => preview(drop(node))}>
      <Card
        withBorder
        shadow="xs"
        radius="md"
        mb="sm"
        p="sm"
        style={{
          opacity: isDragging ? 0.4 : 1,
          border: isOver ? "2px dashed #ee374d" : undefined,
          backgroundColor: isOver ? "#fff5f5" : "#ffffff",
          transition: "all 0.15s ease",
        }}
      >
        <Group position="apart">
          <Group spacing="sm">
            <div
              ref={drag}
              style={{
                cursor: field.isRemovable === false ? "not-allowed" : "grab",
                display: "flex",
                alignItems: "center",
                color: "#94a3b8",
              }}
            >
              <IconGripVertical size={16} />
            </div>
            <Box
              sx={{
                padding: "6px",
                borderRadius: "6px",
                backgroundColor: "#fff0f1",
                color: "#ee374d",
                display: "flex",
              }}
            >
              <FieldIcon size={16} />
            </Box>
            <Stack spacing={0}>
              <Text size="sm" fw={600}>
                {field.label}
                {field.isRequired && (
                  <Text span c="red">
                    {" "}
                    *
                  </Text>
                )}
              </Text>
              <Text size="xs" c="dimmed">
                {field.type} {field.validation?.required && "• Required"}
                {/* {field.dynamicOptions && ` • Dynamic`} */}
              </Text>
            </Stack>
          </Group>
          <Group spacing="xs">
            <ActionIcon
              variant="light"
              color="red"
              size="sm"
              onClick={() => onEdit(field)}
            >
              <IconEdit size={14} />
            </ActionIcon>
            {field.isRemovable !== false && (
              <ActionIcon
                variant="light"
                color="red"
                size="sm"
                onClick={() => onRemove(field.id)}
              >
                <IconTrash size={14} />
              </ActionIcon>
            )}
          </Group>
        </Group>
      </Card>
    </div>
  );
};

// Canvas Drop Zone
const CanvasDropZone: React.FC<{
  onDrop: (
    fieldType: FieldType,
    defaultLabel: string,
    defaultPlaceholder: string,
  ) => void;
  children: React.ReactNode;
}> = ({ onDrop, children }) => {
  const [{ isOver }, drop] = useDrop({
    accept: "field",
    drop: (
      item: DragItem & { defaultLabel?: string; defaultPlaceholder?: string },
    ) => {
      if (item.fieldType && item.defaultLabel && item.defaultPlaceholder) {
        onDrop(item.fieldType, item.defaultLabel, item.defaultPlaceholder);
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  return (
    <div
      ref={drop}
      style={{
        minHeight: "440px",
        border: isOver ? "2px dashed #ee374d" : "2px dashed #e2e8f0",
        borderRadius: "12px",
        backgroundColor: isOver ? "#fff5f5" : "#fafafa",
        transition: "all 0.2s",
        padding: "16px",
      }}
    >
      {children}
    </div>
  );
};

// Edit Field Modal Component
const EditFieldModal: React.FC<{
  field: FormField | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (field: FormField) => void;
}> = ({ field, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState<FormField>({
    id: "",
    type: "text",
    label: "",
    placeholder: "",
    options: [],
    validation: {},
    isRemovable: true,
  });

  useEffect(() => {
    if (field) {
      setFormData(field);
    }
  }, [field]);

  const handleSave = () => {
    onSave(formData);
    onClose();
  };

  const updateValidation = (updates: Partial<Validation>) => {
    setFormData((prev) => ({
      ...prev,
      validation: { ...prev.validation, ...updates },
    }));
  };

  const isDefaultField = field?.isRemovable === false;

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title={<Title size="lg">Edit {field?.type} Field</Title>}
      size="lg"
      radius="md"
    >
      <Stack>
        <TextInput
          label="Label"
          value={formData.label}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, label: e.target.value }))
          }
          placeholder="Enter field label"
          required
          disabled={isDefaultField}
        />

        <TextInput
          label="Placeholder"
          value={formData.placeholder || ""}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, placeholder: e.target.value }))
          }
          placeholder="Enter placeholder text"
          disabled={isDefaultField}
        />

        {formData.type === "select" && !formData.dynamicOptions && (
          <TextInput
            label="Options (comma-separated)"
            value={formData.options?.join(", ") || ""}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                options: e.target.value
                  .split(",")
                  .map((opt) => opt.trim())
                  .filter(Boolean),
              }))
            }
            placeholder="Option 1, Option 2, Option 3"
            disabled={isDefaultField}
          />
        )}

        {formData.dynamicOptions && (
          <Alert color="red" icon={<IconRefresh size={16} />}>
            This field has dynamic options loaded from the database.
            {formData.dynamicOptions.type === "service" &&
              " Services will be filtered based on the selected branch."}
          </Alert>
        )}

        <Divider label="Validation" labelPosition="center" />

        <Switch
          color="red"
          label="Required"
          checked={formData.validation?.required || false}
          onChange={(e) =>
            updateValidation({ required: e.currentTarget.checked })
          }
          disabled={isDefaultField && formData.isRequired}
        />

        {formData.type !== "date" && formData.type !== "number" && (
          <Group grow>
            <NumberInput
              label="Min Length"
              value={formData.validation?.min_length || ""}
              onChange={(value) =>
                updateValidation({
                  min_length: value ? Number(value) : undefined,
                })
              }
              placeholder="No minimum"
              min={0}
              disabled={isDefaultField}
            />

            <NumberInput
              label="Max Length"
              value={formData.validation?.max_length || ""}
              onChange={(value) =>
                updateValidation({
                  max_length: value ? Number(value) : undefined,
                })
              }
              placeholder="No maximum"
              min={1}
              disabled={isDefaultField}
            />
          </Group>
        )}

        {formData.type !== "date" && formData.type !== "number" && (
          <TextInput
            label="Pattern (Regex)"
            value={formData.validation?.pattern || ""}
            onChange={(e) =>
              updateValidation({ pattern: e.target.value || undefined })
            }
            placeholder="Enter regex pattern"
            disabled={isDefaultField}
          />
        )}

        <TextInput
          label="Custom Error Message"
          value={formData.validation?.custom_message || ""}
          onChange={(e) =>
            updateValidation({ custom_message: e.target.value || undefined })
          }
          placeholder="Enter custom error message"
          disabled={isDefaultField}
        />

        {isDefaultField && (
          <Alert color="red">
            This is a required system field and cannot be structurally modified.
          </Alert>
        )}

        <Group position="right" mt="md">
          <Button variant="outline" color="gray" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>Save</Button>
        </Group>
      </Stack>
    </Modal>
  );
};

// Interactive Live Form Preview Component
export const FormPreview: React.FC<{
  title: string;
  description: string;
  fields: FormField[];
  branches: Branch[];
  services: Service[];
  formType: "dynamic" | "single_branch";
  branchId?: string;
  styles: FormStyles;
  hideBorder?: boolean;
  iframeHeight?: string;
}> = ({
  title,
  description,
  fields,
  branches,
  services,
  formType,
  branchId,
  styles,
  hideBorder = false,
  iframeHeight = "660px",
}) => {
  const getShadowStyle = (shadow: string) => {
    switch (shadow) {
      case "sm":
        return "0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)";
      case "md":
        return "0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)";
      case "lg":
        return "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)";
      case "xl":
        return "0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)";
      default:
        return "none";
    }
  };

  const themeStyles = `
    :root {
      --bg-page: ${styles.backgroundColor};
      --bg-card: ${styles.cardBackgroundColor};
      --bg-input: ${styles.inputBgColor};
      --text-primary: ${styles.textColor};
      --text-secondary: ${styles.subtextColor};
      --border-color: ${styles.borderColor};
      --focus-border-color: ${styles.focusBorderColor};
      --accent-color: ${styles.primaryColor};
      --accent-text-color: ${styles.primaryTextColor};
      --font-family: ${styles.fontFamily};
      --font-size: ${styles.fontSize}px;
      --label-weight: ${styles.labelWeight};
      --border-radius: ${styles.borderRadius}px;
      --input-padding: ${styles.inputPadding}px;
      --border-width: ${styles.borderWidth}px;
      --box-shadow: ${getShadowStyle(styles.boxShadow)};
    }
  `;

  const servicesDataJson = JSON.stringify(services);

  const fieldsHtml = fields
    .map((field) => {
      const isRequired = field.isRequired || field.validation?.required;
      const validationAttrs = field.validation
        ? [
            isRequired ? "required" : "",
            field.validation.min_length
              ? `minlength="${field.validation.min_length}"`
              : "",
            field.validation.max_length
              ? `maxlength="${field.validation.max_length}"`
              : "",
            field.validation.pattern
              ? `pattern="${field.validation.pattern}"`
              : "",
            field.validation.custom_message
              ? `title="${field.validation.custom_message}"`
              : "",
          ]
            .filter(Boolean)
            .join(" ")
        : isRequired
          ? "required"
          : "";

      if (field.id === "branch" && field.dynamicOptions?.type === "branch") {
        const branchOptions = branches
          .map((b) => `<option value="${b.id}">${b.name}</option>`)
          .join("");
        return `
        <div class="form-field">
          <label for="${field.id}">${field.label}${isRequired ? ' <span class="required-star">*</span>' : ""}</label>
          <select id="${field.id}" name="${field.id}" onchange="handleBranchSelect(this.value)" ${validationAttrs}>
            <option value="">Select a branch</option>
            ${branchOptions}
          </select>
          <div class="error-message" id="${field.id}-error"></div>
        </div>
      `;
      }

      if (field.id === "service" && field.dynamicOptions?.type === "service") {
        let serviceOptionsHtml = `<option value="">Select a service</option>`;
        let disabledAttr = formType === "single_branch" ? "" : "disabled";

        if (formType === "single_branch" && branchId) {
          serviceOptionsHtml += services
            .filter((s) => s.branch_id === branchId)
            .map((s) => `<option value="${s.id}">${s.name}</option>`)
            .join("");
        }

        return `
        <div class="form-field">
          <label for="${field.id}">${field.label}${isRequired ? ' <span class="required-star">*</span>' : ""}</label>
          <select id="${field.id}" name="${field.id}" ${disabledAttr} ${validationAttrs}>
            ${serviceOptionsHtml}
          </select>
          <div class="error-message" id="${field.id}-error"></div>
        </div>
      `;
      }

      if (field.id === "contact_method") {
        return `
        <div class="form-field">
          <label for="${field.id}">${field.label}${isRequired ? ' <span class="required-star">*</span>' : ""}</label>
          <select id="${field.id}" name="${field.id}" ${validationAttrs}>
            <option value="">Select preferred contact method</option>
            <option value="mobile">Mobile Number</option>
            <option value="email">Email</option>
          </select>
          <div class="error-message" id="${field.id}-error"></div>
        </div>
      `;
      }

      if (field.id === "email") {
        return `
        <div class="form-field" id="email-field">
          <label for="${field.id}">${field.label}${isRequired ? ' <span class="required-star">*</span>' : ""}</label>
          <input type="email" id="${field.id}" name="${field.id}" placeholder="${
            field.placeholder || "Enter email"
          }" ${validationAttrs}>
          <div class="error-message" id="${field.id}-error"></div>
        </div>
      `;
      }

      if (field.id === "mobile_number") {
        return `
        <div class="form-field" id="mobile-field">
          <label for="${field.id}">${field.label}${isRequired ? ' <span class="required-star">*</span>' : ""}</label>
          <div class="mobile-input-container">
            <span class="country-code">+91</span>
            <input type="tel" id="${field.id}" name="${
              field.id
            }" placeholder="${
              field.placeholder || "Enter 10-digit mobile number"
            }" maxlength="10" pattern="[6-9][0-9]{9}" oninput="this.value = this.value.replace(/[^0-9]/g, '')" ${validationAttrs}>
          </div>
          <div class="error-message" id="${field.id}-error"></div>
        </div>
      `;
      }

      switch (field.type) {
        case "textarea":
          return `
          <div class="form-field">
            <label for="${field.id}">${field.label}${isRequired ? ' <span class="required-star">*</span>' : ""}</label>
            <textarea id="${field.id}" name="${field.id}" placeholder="${
              field.placeholder || ""
            }" ${validationAttrs} rows="4"></textarea>
            <div class="error-message" id="${field.id}-error"></div>
          </div>
        `;
        case "select": {
          const optionsHtml =
            field.options
              ?.map((opt) => `<option value="${opt}">${opt}</option>`)
              .join("") || "";
          return `
          <div class="form-field">
            <label for="${field.id}">${field.label}${isRequired ? ' <span class="required-star">*</span>' : ""}</label>
            <select id="${field.id}" name="${field.id}" ${validationAttrs}>
              <option value="">Select an option</option>
              ${optionsHtml}
            </select>
            <div class="error-message" id="${field.id}-error"></div>
          </div>
        `;
        }
        default:
          return `
          <div class="form-field">
            <label for="${field.id}">${field.label}${isRequired ? ' <span class="required-star">*</span>' : ""}</label>
            <input type="${field.type}" id="${field.id}" name="${
              field.id
            }" placeholder="${field.placeholder || ""}" ${validationAttrs}>
            <div class="error-message" id="${field.id}-error"></div>
          </div>
        `;
      }
    })
    .join("");

  const hiddenBranchInput =
    formType === "single_branch" && branchId
      ? `<input type="hidden" name="branch" value="${branchId}" />`
      : "";

  const htmlContent = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8">
      <link rel="preconnect" href="https://fonts.googleapis.com">
      <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Poppins:wght@400;500;600;700&family=Roboto:wght@400;500;700&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap" rel="stylesheet">
      <style>
        ${themeStyles}
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
          font-family: var(--font-family);
          background: var(--bg-page);
          padding: 24px;
          color: var(--text-primary);
          font-size: var(--font-size);
          transition: background 0.3s ease, color 0.3s ease;
        }
        .form-container {
          max-width: 100%;
          background: var(--bg-card);
          border-radius: var(--border-radius);
          border: var(--border-width) solid var(--border-color);
          box-shadow: var(--box-shadow);
          padding: 24px;
          transition: all 0.3s ease;
        }
        .form-header {
          padding-bottom: 16px;
          border-bottom: 1px solid var(--border-color);
          margin-bottom: 20px;
        }
        .form-title {
          font-size: 1.4em;
          font-weight: 700;
          margin-bottom: 6px;
          color: var(--text-primary);
          letter-spacing: -0.02em;
        }
        .form-description {
          color: var(--text-secondary);
          font-size: 0.9em;
          line-height: 1.5;
        }
        .form-field { margin-bottom: 16px; }
        label {
          display: block;
          margin-bottom: 6px;
          font-weight: var(--label-weight);
          font-size: 0.9em;
          color: var(--text-primary);
        }
        .required-star { color: #ee374d; margin-left: 2px; }
        input, textarea, select {
          width: 100%;
          padding: var(--input-padding);
          border: var(--border-width) solid var(--border-color);
          border-radius: var(--border-radius);
          font-size: var(--font-size);
          font-family: inherit;
          background: var(--bg-input);
          color: var(--text-primary);
          transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
        }
        input:focus, textarea:focus, select:focus {
          outline: none;
          border-color: var(--focus-border-color);
          box-shadow: 0 0 0 3px rgba(238, 55, 77, 0.15);
        }
        .error-message { color: #ee374d; font-size: 0.8em; margin-top: 4px; display: none; }
        .submit-btn {
          width: 100%;
          padding: calc(var(--input-padding) + 2px) 16px;
          background: var(--accent-color);
          color: var(--accent-text-color);
          border: none;
          border-radius: var(--border-radius);
          font-size: 0.98em;
          font-weight: 600;
          font-family: inherit;
          cursor: pointer;
          margin-top: 10px;
          transition: all 0.2s ease;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        .submit-btn:hover {
          filter: brightness(1.08);
          transform: translateY(-1px);
        }
        .submit-btn:active {
          transform: translateY(0);
        }
        .mobile-input-container { position: relative; display: flex; align-items: center; }
        .country-code {
          position: absolute;
          left: var(--input-padding);
          color: var(--text-secondary);
          font-size: 0.9em;
          z-index: 1;
        }
        .mobile-input-container input { padding-left: calc(var(--input-padding) + 32px); }
        .success-banner {
          display: none;
          padding: 14px;
          background-color: #d1fae5;
          color: #065f46;
          border: 1px solid #a7f3d0;
          border-radius: var(--border-radius);
          margin-bottom: 18px;
          font-size: 0.9em;
          font-weight: 500;
          text-align: center;
        }
      </style>
    </head>
    <body>
      <div class="form-container">
        <div class="form-header">
          <h1 class="form-title">${title || "Form Preview"}</h1>
          ${description ? `<p class="form-description">${description}</p>` : ""}
        </div>
        <div id="success-banner" class="success-banner">Thank you! Your submission was recorded successfully.</div>
        <form id="live-form" class="form-body" onsubmit="handleSubmit(event)">
          ${hiddenBranchInput}
          ${fieldsHtml}
          <button type="submit" class="submit-btn">Submit Form</button>
        </form>
      </div>

      <script>
        const services = ${servicesDataJson};

        function handleBranchSelect(branchId) {
          const serviceSelect = document.getElementById('service');
          if (!serviceSelect) return;

          if (!branchId) {
            serviceSelect.innerHTML = '<option value="">Select a service</option>';
            serviceSelect.disabled = true;
            return;
          }

          const filtered = services.filter(s => s.branch_id === branchId || s.branch_code === branchId);
          let options = '<option value="">Select a service</option>';
          filtered.forEach(s => {
            options += '<option value="' + s.id + '">' + s.name + '</option>';
          });
          serviceSelect.innerHTML = options;
          serviceSelect.disabled = false;
        }

        function handleSubmit(e) {
          e.preventDefault();
          const form = e.target;
          if (form.checkValidity()) {
            document.getElementById('success-banner').style.display = 'block';
            form.reset();
            setTimeout(() => {
              document.getElementById('success-banner').style.display = 'none';
            }, 4000);
          }
        }
      </script>
    </body>
    </html>
  `;

  const iframeElement = (
    <iframe
      srcDoc={htmlContent}
      style={{ width: "100%", height: iframeHeight, border: "none" }}
      title="Interactive Form Preview"
    />
  );

  if (hideBorder) {
    return iframeElement;
  }

  return (
    <Card
      withBorder
      shadow="sm"
      radius="md"
      p={0}
      style={{ overflow: "hidden" }}
    >
      {iframeElement}
    </Card>
  );
};

// Embed Snippet Component (Modified to use Select dropdown instead of Tabs)
export const EmbedSnippet: React.FC<{ formId: string }> = ({ formId }) => {
  const [selectedFormat, setSelectedFormat] = useState<string>("script");
  const [rawCode, setRawCode] = useState<string>("");
  const [loadingCode, setLoadingCode] = useState(false);
  const [landingPageUrl, setLandingPageUrl] = useState<string | null>(null);
  const [loadingUrl, setLoadingUrl] = useState(false);
  const qrCanvasRef = React.useRef<HTMLCanvasElement>(null);
  const companyId = useAppSelector(
    (state: RootState) => state.session?.company_id,
  );

  useEffect(() => {
    const fetchLandingPageUrl = async () => {
      if (!companyId || !formId) return;
      setLoadingUrl(true);
      try {
        const q = query(
          collection(db, "customlandingpages"),
          where("company_id", "==", companyId),
          where("form_id", "==", formId),
          limit(1),
        );
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          const pageData = snapshot.docs[0].data();
          const baseUrl =
            process.env.REACT_APP_FIREBASE_FUNCTION_BASE_URL ||
            "https://asia-south1-bench-dev-93520.cloudfunctions.net";
          setLandingPageUrl(`${baseUrl}/pageHtml?page_id=${pageData.page_id}`);
        }
      } catch (err) {
        console.error("Error fetching landing page URL:", err);
      } finally {
        setLoadingUrl(false);
      }
    };
    fetchLandingPageUrl();
  }, [companyId, formId]);

  useEffect(() => {
    if (landingPageUrl && qrCanvasRef.current) {
      QRCode.toCanvas(qrCanvasRef.current, landingPageUrl, {
        width: 150,
        margin: 2,
        color: { dark: "#0f172a", light: "#ffffff" },
      }).catch(console.error);
    }
  }, [landingPageUrl, selectedFormat]);

  const downloadQR = () => {
    if (!qrCanvasRef.current) return;
    const url = qrCanvasRef.current.toDataURL("image/png");
    const link = document.createElement("a");
    link.href = url;
    link.download = `Form_${formId}_QRCode.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const widgetScript = `<div id="form-container"></div>
<script src="${process.env.REACT_APP_CURRENT_DOMAIN_NAME}/FormBuilder/formWidgetJs?form_id=${formId}"></script>`;

  const iframeSnippet = `<iframe src="${process.env.REACT_APP_CURRENT_DOMAIN_NAME}/FormBuilder/formHtml?form_id=${formId}" width="100%" height="700px" style="border:none;"></iframe>`;

  const sourceCodeUrl = `${process.env.REACT_APP_CURRENT_DOMAIN_NAME}/FormBuilder/formHtml?form_id=${formId}&raw=true`;

  const fetchSourceCode = async () => {
    setLoadingCode(true);
    try {
      const res = await fetch(sourceCodeUrl);
      if (res.ok) {
        const text = await res.text();
        setRawCode(text);
      } else {
        setRawCode("Failed to load source code.");
      }
    } catch (e) {
      setRawCode(
        "Error fetching source code. Please try opening the link directly.",
      );
    }
    setLoadingCode(false);
  };

  return (
    <Card
      withBorder
      shadow="sm"
      radius="lg"
      p="lg"
      // mih={540}
      style={{
        display: "flex",
        flexDirection: "column",
        backgroundColor: "var(--mantine-color-body)",
      }}
    >
      {/* Header Section */}
      <Group align="flex-start" mb="md">
        <Box>
          <Text size="md" fw={700} c="dark.8">
            Share & Embed
          </Text>
          <Text size="xs" c="dimmed">
            Choose how you want to deploy or integrate your form
          </Text>
        </Box>
        <ThemeIcon variant="light" color="indigo" radius="md" size="md">
          <IconCode size={18} />
        </ThemeIcon>
      </Group>

      {/* Modern Format Switcher */}
      <SegmentedControl
        fullWidth
        radius="md"
        size="xs"
        value={selectedFormat}
        onChange={(val) => setSelectedFormat(val || "script")}
        data={[
          { value: "script", label: "JS Widget" },
          { value: "iframe", label: "iFrame" },
          // { value: "landing", label: "Landing Page" },
          { value: "source", label: "Source Code" },
        ]}
        mb="md"
      />

      {/* Panel Content Wrapper */}
      <Box style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        {/* JS WIDGET */}
        {selectedFormat === "script" && (
          <Stack justify="space-between" style={{ flex: 1 }} spacing="md">
            <Box>
              <Text size="xs" c="dimmed" mb="xs">
                Copy and paste this script directly where you want the form to
                render:
              </Text>
              <Paper
                withBorder
                radius="md"
                p="xs"
                bg="dark.8"
                style={{ position: "relative" }}
              >
                <ScrollArea mah={220} type="auto">
                  <Code
                    block
                    c="gray.2"
                    bg="transparent"
                    style={{ fontSize: "12px", whiteSpace: "pre-wrap" }}
                  >
                    {widgetScript}
                  </Code>
                </ScrollArea>
              </Paper>
            </Box>

            <CopyButton value={widgetScript}>
              {({ copied, copy }) => (
                <Button fullWidth radius="md" onClick={copy}>
                  {copied ? "Copied to Clipboard!" : "Copy Widget Script"}
                </Button>
              )}
            </CopyButton>
          </Stack>
        )}

        {/* IFRAME */}
        {selectedFormat === "iframe" && (
          <Stack justify="space-between" style={{ flex: 1 }} spacing="md">
            <Box>
              <Text size="xs" c="dimmed" mb="xs">
                Ideal for site builders like WordPress, Wix, or Squarespace that
                restrict custom JavaScript:
              </Text>
              <Paper
                withBorder
                radius="md"
                p="xs"
                bg="dark.8"
                style={{ position: "relative" }}
              >
                <ScrollArea mah={220} type="auto">
                  <Code
                    block
                    c="gray.2"
                    bg="transparent"
                    style={{ fontSize: "12px", whiteSpace: "pre-wrap" }}
                  >
                    {iframeSnippet}
                  </Code>
                </ScrollArea>
              </Paper>
            </Box>

            <CopyButton value={iframeSnippet}>
              {({ copied, copy }) => (
                <Button fullWidth radius="md" onClick={copy}>
                  {copied ? "Copied to Clipboard!" : "Copy iFrame Embed"}
                </Button>
              )}
            </CopyButton>
          </Stack>
        )}

        {/* LANDING PAGE */}
        {selectedFormat === "landing" && (
          <Box style={{ flex: 1 }}>
            <Text size="xs" c="dimmed" mb="sm">
              Direct link and QR code for visitors to view your standalone form:
            </Text>

            {loadingUrl ? (
              <Center h={240}>
                <Stack align="center" spacing="xs">
                  <Loader size="sm" color="indigo" />
                  <Text size="xs" c="dimmed">
                    Loading landing page link...
                  </Text>
                </Stack>
              </Center>
            ) : landingPageUrl ? (
              <Stack spacing="md">
                <Paper
                  withBorder
                  p="sm"
                  radius="md"
                  bg="gray.0"
                  style={{ display: "flex", justifyContent: "center" }}
                >
                  <Group spacing="xl" align="center">
                    <Box
                      p={4}
                      bg="white"
                      style={{
                        borderRadius: "8px",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                        display: "flex",
                      }}
                    >
                      <canvas ref={qrCanvasRef} />
                    </Box>
                    <Button
                      variant="default"
                      size="xs"
                      radius="md"
                      onClick={downloadQR}
                    >
                      Save QR Code
                    </Button>
                  </Group>
                </Paper>

                <Code
                  block
                  p="xs"
                  style={{
                    fontSize: "12px",
                    wordBreak: "break-all",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {landingPageUrl}
                </Code>

                <Group grow spacing="xs">
                  <Button
                    component="a"
                    href={landingPageUrl}
                    target="_blank"
                    variant="default"
                    radius="md"
                  >
                    Open Link
                  </Button>
                  <CopyButton value={landingPageUrl}>
                    {({ copied, copy }) => (
                      <Button radius="md" onClick={copy}>
                        {copied ? "Copied URL!" : "Copy URL"}
                      </Button>
                    )}
                  </CopyButton>
                </Group>
              </Stack>
            ) : (
              <Center h={200}>
                <Stack align="center" spacing="xs">
                  <ThemeIcon size="lg" radius="xl" color="gray" variant="light">
                    <IconWorld size={20} />
                  </ThemeIcon>
                  <Text size="xs" c="dimmed" ta="center">
                    No landing page saved yet. Build and save one in the Landing
                    Page Builder tab.
                  </Text>
                </Stack>
              </Center>
            )}
          </Box>
        )}

        {/* SOURCE CODE */}
        {selectedFormat === "source" && (
          <Stack justify="space-between" style={{ flex: 1 }} spacing="xs">
            <Text size="xs" c="dimmed">
              Want to self-host and customize every detail? Export the complete
              HTML, CSS, and JS bundle.
            </Text>

            {!rawCode ? (
              <Group spacing="xs" mt="sm" align="center">
                <Button
                  radius="md"
                  onClick={fetchSourceCode}
                  loading={loadingCode}
                >
                  Generate Source Code
                </Button>

                <CopyButton value={sourceCodeUrl}>
                  {({ copied, copy }) => (
                    <Button radius="md" onClick={copy}>
                      {copied ? "Link Copied!" : "Copy Source Code Link"}
                    </Button>
                  )}
                </CopyButton>
              </Group>
            ) : (
              <Stack spacing="xs">
                <CopyButton value={rawCode}>
                  {({ copied, copy }) => (
                    <Button
                      fullWidth
                      radius="md"
                      color={copied ? "teal" : "indigo"}
                      variant={copied ? "filled" : "light"}
                      onClick={copy}
                    >
                      {copied ? "Source Code Copied!" : "Copy Full Source Code"}
                    </Button>
                  )}
                </CopyButton>

                <Paper withBorder radius="md" p="xs" bg="dark.8">
                  <ScrollArea mah={180} type="auto">
                    <Code
                      block
                      c="gray.2"
                      bg="transparent"
                      style={{
                        whiteSpace: "pre-wrap",
                        fontSize: "11px",
                      }}
                    >
                      {rawCode}
                    </Code>
                  </ScrollArea>
                </Paper>

                <Text size="xs" c="dimmed" ta="center">
                  If the preview failed to load, you can{" "}
                  <Anchor
                    href={sourceCodeUrl}
                    target="_blank"
                    rel="noreferrer"
                    size="xs"
                  >
                    open it directly in a new tab
                  </Anchor>
                  .
                </Text>
              </Stack>
            )}
          </Stack>
        )}
      </Box>
    </Card>
  );
};

// Main Form Builder Component
const FormBuilder: React.FC = () => {
  const { formId: urlFormId } = useParams<{ formId: string }>();
  const navigate = useNavigate();

  const companyId = useAppSelector(
    (state: RootState) => state.session?.company_id,
  );

  const [formStyles, setFormStyles] = useState<FormStyles>({
    primaryColor: "#dc3545",
    primaryTextColor: "#ffffff",
    backgroundColor: "#ffffff",
    cardBackgroundColor: "#ffffff",
    textColor: "#1e293b",
    subtextColor: "#64748b",
    inputBgColor: "#ffffff",
    borderColor: "#e2e8f0",
    focusBorderColor: "#dc3545",
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    fontSize: 14,
    labelWeight: 600,
    borderRadius: 8,
    inputPadding: 10,
    borderWidth: 1,
    boxShadow: "sm",
  });

  const [formConfig, setFormConfig] = useState<{
    allowed_domains: string[];
    title: string;
    description: string;
    fields: FormField[];
    send_to: string;
    form_type: "dynamic" | "single_branch";
    branch_id?: string;
  }>({
    allowed_domains: [],
    title: "Contact Form",
    description:
      "Please fill out the form below and we will get back to you soon.",
    fields: getDefaultFields(),
    send_to: "enquiry",
    form_type: "dynamic",
  });

  const [editingField, setEditingField] = useState<FormField | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEmbedModalOpen, setIsEmbedModalOpen] = useState(false);
  const [savedFormId, setSavedFormId] = useState<string | null>(
    urlFormId || null,
  );
  const [firestoreDocId, setFirestoreDocId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Fetch form if editing
  useEffect(() => {
    const fetchExistingForm = async () => {
      if (urlFormId && companyId) {
        setIsSaving(true);
        try {
          const q = query(
            collection(db, "customforms"),
            where("form_id", "==", urlFormId),
            where("company_id", "==", companyId),
          );
          const snapshot = await getDocs(q);
          if (!snapshot.empty) {
            const docSnap = snapshot.docs[0];
            const data = docSnap.data();
            setFirestoreDocId(docSnap.id);
            setFormConfig({
              title: data.title || "",
              send_to: data.send_to || "",
              description: data.description || "",
              fields: data.fields || [],
              allowed_domains: data.allowed_domains || [],
              form_type: data.form_type || "dynamic",
              branch_id: data.branch_id || "",
            });
            if (data.styles) {
              setFormStyles(data.styles);
            }
          }
        } catch (error) {
          console.error("Error fetching form:", error);
        } finally {
          setIsSaving(false);
        }
      }
    };
    fetchExistingForm();
  }, [urlFormId, companyId]);

  // Dynamic branch & service data
  const [branches, setBranches] = useState<Branch[]>([]);
  const [services, setServices] = useState<Service[]>([]);

  useEffect(() => {
    const loadData = async () => {
      if (!companyId) return;
      const [branchesData, servicesData] = await Promise.all([
        fetchBranches(companyId),
        fetchServices(),
      ]);
      setBranches(branchesData);
      setServices(servicesData);
    };

    loadData();
  }, [companyId]);

  useEffect(() => {
    if (branches.length > 0) {
      setFormConfig((prev) => ({
        ...prev,
        fields: prev.fields.map((field) => {
          if (
            field.id === "branch" &&
            field.dynamicOptions?.type === "branch"
          ) {
            return {
              ...field,
              options: branches.map((branch) => branch.name),
            };
          }
          return field;
        }),
      }));
    }
  }, [branches]);

  const handleDrop = (
    fieldType: FieldType,
    defaultLabel: string,
    defaultPlaceholder: string,
  ) => {
    const baseField: FormField = {
      id: nanoid(),
      type: fieldType,
      label: defaultLabel,
      placeholder: defaultPlaceholder,
      validation: { required: false },
      isRemovable: true,
    };

    if (fieldType === "select") {
      baseField.options = ["Option 1", "Option 2", "Option 3"];
    }

    setFormConfig((prev) => ({
      ...prev,
      fields: [...prev.fields, baseField],
    }));
  };

  const updateField = (updatedField: FormField) => {
    setFormConfig((prev) => ({
      ...prev,
      fields: prev.fields.map((field) =>
        field.id === updatedField.id ? updatedField : field,
      ),
    }));
    setEditingField(null);
  };

  const removeField = (id: string) => {
    const fieldToRemove = formConfig.fields.find((field) => field.id === id);
    if (fieldToRemove?.isRemovable === false) {
      return;
    }

    setFormConfig((prev) => ({
      ...prev,
      fields: prev.fields.filter((field) => field.id !== id),
    }));
  };

  const reAddOptionalField = (fieldKey: string) => {
    const preset = OPTIONAL_FIELD_PRESETS[fieldKey];
    if (preset && !formConfig.fields.some((f) => f.id === preset.id)) {
      setFormConfig((prev) => ({
        ...prev,
        fields: [...prev.fields, preset],
      }));
    }
  };

  const moveField = (fromIndex: number, toIndex: number) => {
    setFormConfig((prev) => {
      const newFields = [...prev.fields];
      const [movedField] = newFields.splice(fromIndex, 1);
      newFields.splice(toIndex, 0, movedField);
      return { ...prev, fields: newFields };
    });
  };

  const openEditModal = (field: FormField) => {
    setEditingField(field);
    setIsModalOpen(true);
  };

  const closeEditModal = () => {
    setIsModalOpen(false);
    setEditingField(null);
  };

  const applyThemePreset = (presetKey: string) => {
    const preset = COLOR_THEME_PRESETS[presetKey];
    if (preset) {
      setFormStyles((prev) => ({
        ...prev,
        ...preset,
      }));
    }
  };

  const validateDomain = (domain: string): boolean => {
    const domainRegex =
      /^(?!:\/\/)([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(?::\d+)?(?:\/|$)/;
    const localhostRegex = /^localhost(?::\d+)?$/;

    if (localhostRegex.test(domain)) return true;
    if (!domainRegex.test(domain)) return false;
    if (/^\d+\.\d+\.\d+\.\d+$/.test(domain)) return false;
    return domain.includes(".") || domain === "localhost";
  };

  const saveFormToFirestore = async () => {
    if (!formConfig.title.trim()) {
      setSaveError("Form title is required");
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      const formId = savedFormId || nanoid();

      const customForm: Omit<CustomForm, "created_at"> = {
        company_id: companyId,
        form_id: formId,
        title: formConfig.title,
        send_to: formConfig.send_to,
        description: formConfig.description,
        fields: formConfig.fields,
        styles: formStyles,
        created_by: null,
        is_active: true,
        allowed_domains: formConfig.allowed_domains || [],
        form_type: formConfig.form_type,
        ...(formConfig.branch_id ? { branch_id: formConfig.branch_id } : {}),
      };

      if (firestoreDocId) {
        await updateDoc(doc(db, "customforms", firestoreDocId), {
          ...customForm,
        });
      } else {
        const docRef = await addDoc(collection(db, "customforms"), {
          ...customForm,
          created_at: serverTimestamp(),
        });
        setFirestoreDocId(docRef.id);
      }

      setSavedFormId(formId);
    } catch (error) {
      console.error("Error saving form:", error);
      setSaveError("Failed to save form. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const removedOptionalFields = Object.entries(OPTIONAL_FIELD_PRESETS).filter(
    ([id]) => !formConfig.fields.some((f) => f.id === id),
  );

  const breadcrumbslink = [
    { label: "Form Builder", path: "/formbuilder" },
    { label: firestoreDocId ? "Edit Form" : "Create Form" },
  ];

  return (
    <DndProvider backend={HTML5Backend}>
      <Container size="lg" my="md">
        <Box
          sx={(theme) => ({
            position: "sticky",
            top: 110,
            zIndex: 100,
            backgroundColor: "#FAFAFA",
            paddingTop: theme.spacing.sm,
            marginTop: `-${theme.spacing.sm}`,
          })}
        >
          <SimpleGrid cols={2}>
            <BenchSettingsBreadcrumbs links={breadcrumbslink} />
            <Group position="right">
              {savedFormId && (
                <Button
                  onClick={() => setIsEmbedModalOpen(true)}
                  size="xs"
                  variant="outline"
                  leftIcon={<IconCopy size={16} />}
                >
                  Share & Embed
                </Button>
              )}
              <Button
                onClick={saveFormToFirestore}
                disabled={formConfig.fields.length === 0}
                size="xs"
                color="red"
                sx={{
                  backgroundColor: "#ee374d",
                  "&:hover": { backgroundColor: "#d7283e" },
                }}
              >
                {firestoreDocId ? "Update Form" : "Create Form"}
              </Button>
            </Group>
          </SimpleGrid>
          <Divider my="sm" />
        </Box>

        <BenchTopHeader
          icon={<IconForms />}
          title="Form & Page Builder"
          description="Build custom forms and beautiful landing pages"
        />

        <Box pos="relative">
          <LoadingOverlay visible={isSaving} />

          <Grid mb="xl" gutter="xl" align="flex-start">
            {/* LEFT COLUMN: Settings & Styling */}
            <Grid.Col span={12} lg={4}>
              <Stack spacing="md">
                {/* Form Settings */}
                <Card
                  withBorder
                  shadow="sm"
                  radius="md"
                  sx={{ borderTop: "6px solid #ee374d" }}
                >
                  <Title order={5} mb="sm" sx={{ color: "#1e293b" }}>
                    Form Configuration
                  </Title>
                  <Stack spacing="sm">
                    <TextInput
                      label="Allowed Domains"
                      description="Comma-separated domains (e.g., example.com, mysite.org)."
                      value={formConfig.allowed_domains.join(", ")}
                      onChange={(e) => {
                        const domains = e.target.value
                          .split(",")
                          .map((domain) => domain.trim())
                          .filter(Boolean);

                        setFormConfig((prev) => ({
                          ...prev,
                          allowed_domains: domains,
                        }));
                      }}
                      placeholder="example.com, mysite.org"
                      required
                      // error={
                      //   formConfig.allowed_domains.length === 0
                      //     ? "At least one domain is required"
                      //     : formConfig.allowed_domains.some(
                      //           (domain) => !validateDomain(domain),
                      //         )
                      //       ? "Some domains are invalid format"
                      //       : null
                      // }
                    />

                    <Select
                      label="Form Type"
                      value={formConfig.form_type}
                      onChange={(value) => {
                        const newType =
                          (value as "dynamic" | "single_branch") || "dynamic";
                        setFormConfig((prev) => {
                          let updatedFields = [...prev.fields];
                          if (newType === "single_branch") {
                            updatedFields = updatedFields.filter(
                              (f) => f.id !== "branch",
                            );
                          } else {
                            if (!updatedFields.some((f) => f.id === "branch")) {
                              const branchField = getDefaultFields().find(
                                (f) => f.id === "branch",
                              );
                              if (branchField) {
                                updatedFields.splice(1, 0, branchField);
                              }
                            }
                          }
                          return {
                            ...prev,
                            form_type: newType,
                            fields: updatedFields,
                          };
                        });
                      }}
                      data={[
                        {
                          value: "dynamic",
                          label: "Dynamic Branch Selection",
                        },
                        {
                          value: "single_branch",
                          label: "Single Branch Fixed",
                        },
                      ]}
                    />

                    {formConfig.form_type === "single_branch" && (
                      <Select
                        label="Select Branch"
                        placeholder="Choose the fixed branch"
                        required
                        value={formConfig.branch_id || ""}
                        onChange={(value) =>
                          setFormConfig((prev) => ({
                            ...prev,
                            branch_id: value || undefined,
                          }))
                        }
                        data={branches.map((b) => ({
                          value: b.id,
                          label: b.name,
                        }))}
                      />
                    )}

                    <TextInput
                      label="Form Title"
                      value={formConfig.title}
                      onChange={(e) =>
                        setFormConfig((prev) => ({
                          ...prev,
                          title: e.target.value,
                        }))
                      }
                      placeholder="Enter form title"
                      required
                    />

                    <Textarea
                      label="Form Description"
                      value={formConfig.description}
                      onChange={(e) =>
                        setFormConfig((prev) => ({
                          ...prev,
                          description: e.target.value,
                        }))
                      }
                      placeholder="Enter form description"
                      rows={3}
                    />
                  </Stack>
                </Card>

                {/* Advanced Form Styling Customizer */}
                <Card withBorder shadow="sm" radius="md">
                  <Accordion variant="separated" defaultValue="styles">
                    <Accordion.Item value="styles">
                      <Accordion.Control
                        icon={<IconPalette size={20} color="#ee374d" />}
                      >
                        <Group position="apart" pr="sm">
                          <Title order={5}>Customize Form UI</Title>
                        </Group>
                      </Accordion.Control>

                      <Accordion.Panel>
                        {/* Theme Presets */}
                        <Box mb="md">
                          <Group spacing={4} mb={6}>
                            <IconSparkles size={14} color="#f59e0b" />
                            <Text size="xs" fw={600} color="dimmed">
                              Quick Theme Presets
                            </Text>
                          </Group>
                          <Group spacing={6}>
                            <Tooltip label="Modern Crimson">
                              <ActionIcon
                                size="sm"
                                radius="xl"
                                sx={{ backgroundColor: "#dc3545" }}
                                onClick={() =>
                                  applyThemePreset("modern_crimson")
                                }
                              />
                            </Tooltip>
                            <Tooltip label="Deep Emerald">
                              <ActionIcon
                                size="sm"
                                radius="xl"
                                sx={{ backgroundColor: "#059669" }}
                                onClick={() => applyThemePreset("deep_emerald")}
                              />
                            </Tooltip>
                            <Tooltip label="Midnight Indigo">
                              <ActionIcon
                                size="sm"
                                radius="xl"
                                sx={{ backgroundColor: "#4f46e5" }}
                                onClick={() =>
                                  applyThemePreset("midnight_indigo")
                                }
                              />
                            </Tooltip>
                            <Tooltip label="Clean Slate">
                              <ActionIcon
                                size="sm"
                                radius="xl"
                                sx={{ backgroundColor: "#0f172a" }}
                                onClick={() => applyThemePreset("clean_slate")}
                              />
                            </Tooltip>
                            <Tooltip label="Minimal Dark">
                              <ActionIcon
                                size="sm"
                                radius="xl"
                                sx={{
                                  backgroundColor: "#1e293b",
                                  border: "1px solid #3b82f6",
                                }}
                                onClick={() => applyThemePreset("minimal_dark")}
                              />
                            </Tooltip>
                          </Group>
                        </Box>

                        {/* Tabbed Customization */}
                        <Tabs defaultValue="colors">
                          <Tabs.List grow mb="xs">
                            <Tabs.Tab
                              value="colors"
                              icon={<IconPalette size={14} />}
                            >
                              Colors
                            </Tabs.Tab>
                            <Tabs.Tab
                              value="typography"
                              icon={<IconTypography size={14} />}
                            >
                              Font
                            </Tabs.Tab>
                            <Tabs.Tab
                              value="layout"
                              icon={<IconAdjustmentsHorizontal size={14} />}
                            >
                              Layout
                            </Tabs.Tab>
                          </Tabs.List>

                          {/* Tab 1: Colors */}
                          <Tabs.Panel value="colors">
                            <Stack spacing="xs">
                              <Group grow>
                                <ColorInput
                                  label="Button Background"
                                  size="xs"
                                  format="hex"
                                  swatches={[
                                    "#ee374d",
                                    "#dc3545",
                                    "#059669",
                                    "#4f46e5",
                                    "#0f172a",
                                    "#3b82f6",
                                    "#f59e0b",
                                    "#8b5cf6",
                                  ]}
                                  value={formStyles.primaryColor}
                                  onChange={(color) =>
                                    setFormStyles((prev) => ({
                                      ...prev,
                                      primaryColor: color,
                                    }))
                                  }
                                />
                                <ColorInput
                                  label="Button Text"
                                  size="xs"
                                  format="hex"
                                  swatches={[
                                    "#ffffff",
                                    "#f8fafc",
                                    "#f1f5f9",
                                    "#0f172a",
                                    "#1e293b",
                                    "#334155",
                                  ]}
                                  value={formStyles.primaryTextColor}
                                  onChange={(color) =>
                                    setFormStyles((prev) => ({
                                      ...prev,
                                      primaryTextColor: color,
                                    }))
                                  }
                                />
                              </Group>
                              <Group grow>
                                <ColorInput
                                  label="Card Background"
                                  size="xs"
                                  format="hex"
                                  swatches={[
                                    "#ffffff",
                                    "#f8fafc",
                                    "#f1f5f9",
                                    "#1e293b",
                                    "#0f172a",
                                    "#334155",
                                  ]}
                                  value={formStyles.cardBackgroundColor}
                                  onChange={(color) =>
                                    setFormStyles((prev) => ({
                                      ...prev,
                                      cardBackgroundColor: color,
                                    }))
                                  }
                                />
                                <ColorInput
                                  label="Page Background"
                                  size="xs"
                                  format="hex"
                                  swatches={[
                                    "#ffffff",
                                    "#f8fafc",
                                    "#f1f5f9",
                                    "#1e293b",
                                    "#0f172a",
                                    "#334155",
                                  ]}
                                  value={formStyles.backgroundColor}
                                  onChange={(color) =>
                                    setFormStyles((prev) => ({
                                      ...prev,
                                      backgroundColor: color,
                                    }))
                                  }
                                />
                              </Group>
                              <Group grow>
                                <ColorInput
                                  label="Input Background"
                                  size="xs"
                                  format="hex"
                                  swatches={[
                                    "#ffffff",
                                    "#f8fafc",
                                    "#f1f5f9",
                                    "#1e293b",
                                    "#0f172a",
                                    "#334155",
                                  ]}
                                  value={formStyles.inputBgColor}
                                  onChange={(color) =>
                                    setFormStyles((prev) => ({
                                      ...prev,
                                      inputBgColor: color,
                                    }))
                                  }
                                />
                                <ColorInput
                                  label="Text Color"
                                  size="xs"
                                  format="hex"
                                  swatches={[
                                    "#0f172a",
                                    "#1e293b",
                                    "#334155",
                                    "#475569",
                                    "#f8fafc",
                                    "#ffffff",
                                  ]}
                                  value={formStyles.textColor}
                                  onChange={(color) =>
                                    setFormStyles((prev) => ({
                                      ...prev,
                                      textColor: color,
                                    }))
                                  }
                                />
                              </Group>
                              <Group grow>
                                <ColorInput
                                  label="Border Color"
                                  size="xs"
                                  format="hex"
                                  swatches={[
                                    "#e2e8f0",
                                    "#cbd5e1",
                                    "#94a3b8",
                                    "#64748b",
                                    "#475569",
                                    "#334155",
                                  ]}
                                  value={formStyles.borderColor}
                                  onChange={(color) =>
                                    setFormStyles((prev) => ({
                                      ...prev,
                                      borderColor: color,
                                    }))
                                  }
                                />
                                <ColorInput
                                  label="Focus Border"
                                  size="xs"
                                  format="hex"
                                  swatches={[
                                    "#ee374d",
                                    "#dc3545",
                                    "#059669",
                                    "#4f46e5",
                                    "#0f172a",
                                    "#3b82f6",
                                    "#f59e0b",
                                    "#8b5cf6",
                                  ]}
                                  value={formStyles.focusBorderColor}
                                  onChange={(color) =>
                                    setFormStyles((prev) => ({
                                      ...prev,
                                      focusBorderColor: color,
                                    }))
                                  }
                                />
                              </Group>
                            </Stack>
                          </Tabs.Panel>

                          {/* Tab 2: Typography */}
                          <Tabs.Panel value="typography">
                            <Stack spacing="xs">
                              <Select
                                label="Font Family"
                                size="xs"
                                value={formStyles.fontFamily}
                                onChange={(value) =>
                                  setFormStyles((prev) => ({
                                    ...prev,
                                    fontFamily: value || prev.fontFamily,
                                  }))
                                }
                                data={[
                                  {
                                    value:
                                      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                                    label: "System Standard",
                                  },
                                  {
                                    value: "'Inter', sans-serif",
                                    label: "Inter",
                                  },
                                  {
                                    value: "'Poppins', sans-serif",
                                    label: "Poppins",
                                  },
                                  {
                                    value: "'Roboto', sans-serif",
                                    label: "Roboto",
                                  },
                                  {
                                    value: "'Plus Jakarta Sans', sans-serif",
                                    label: "Plus Jakarta Sans",
                                  },
                                ]}
                              />
                              <Box mt="xs">
                                <Text size="xs" fw={500} mb={4}>
                                  Font Size ({formStyles.fontSize}px)
                                </Text>
                                <Slider
                                  color="red"
                                  value={formStyles.fontSize}
                                  onChange={(val) =>
                                    setFormStyles((prev) => ({
                                      ...prev,
                                      fontSize: val,
                                    }))
                                  }
                                  min={12}
                                  max={20}
                                  step={1}
                                  size="sm"
                                />
                              </Box>
                              <Box mt="xs">
                                <Text size="xs" fw={500} mb={4}>
                                  Label Weight ({formStyles.labelWeight})
                                </Text>
                                <Slider
                                  color="red"
                                  value={formStyles.labelWeight}
                                  onChange={(val) =>
                                    setFormStyles((prev) => ({
                                      ...prev,
                                      labelWeight: val,
                                    }))
                                  }
                                  min={400}
                                  max={700}
                                  step={100}
                                  size="sm"
                                />
                              </Box>
                            </Stack>
                          </Tabs.Panel>

                          {/* Tab 3: Layout */}
                          <Tabs.Panel value="layout">
                            <Stack spacing="xs">
                              <Select
                                label="Card Shadow"
                                size="xs"
                                value={formStyles.boxShadow}
                                onChange={(value) =>
                                  setFormStyles((prev) => ({
                                    ...prev,
                                    boxShadow:
                                      (value as FormStyles["boxShadow"]) ||
                                      "none",
                                  }))
                                }
                                data={[
                                  { value: "none", label: "None" },
                                  { value: "sm", label: "Small" },
                                  { value: "md", label: "Medium" },
                                  { value: "lg", label: "Large" },
                                  { value: "xl", label: "Extra Large" },
                                ]}
                              />
                              <Box mt="xs">
                                <Text size="xs" fw={500} mb={4}>
                                  Border Radius ({formStyles.borderRadius}px)
                                </Text>
                                <Slider
                                  color="red"
                                  value={formStyles.borderRadius}
                                  onChange={(val) =>
                                    setFormStyles((prev) => ({
                                      ...prev,
                                      borderRadius: val,
                                    }))
                                  }
                                  min={0}
                                  max={24}
                                  step={2}
                                  size="sm"
                                />
                              </Box>
                              <Box mt="xs">
                                <Text size="xs" fw={500} mb={4}>
                                  Input Padding ({formStyles.inputPadding}px)
                                </Text>
                                <Slider
                                  color="red"
                                  value={formStyles.inputPadding}
                                  onChange={(val) =>
                                    setFormStyles((prev) => ({
                                      ...prev,
                                      inputPadding: val,
                                    }))
                                  }
                                  min={6}
                                  max={18}
                                  step={1}
                                  size="sm"
                                />
                              </Box>
                              <Box mt="xs">
                                <Text size="xs" fw={500} mb={4}>
                                  Border Width ({formStyles.borderWidth}px)
                                </Text>
                                <Slider
                                  color="red"
                                  value={formStyles.borderWidth}
                                  onChange={(val) =>
                                    setFormStyles((prev) => ({
                                      ...prev,
                                      borderWidth: val,
                                    }))
                                  }
                                  min={0}
                                  max={4}
                                  step={1}
                                  size="sm"
                                />
                              </Box>
                            </Stack>
                          </Tabs.Panel>
                        </Tabs>
                      </Accordion.Panel>
                    </Accordion.Item>
                  </Accordion>
                </Card>
              </Stack>
            </Grid.Col>

            {/* CENTER COLUMN: Live Preview (Sticky) */}
            <Grid.Col span={12} lg={4}>
              <Box sx={{ position: "sticky", top: 125, zIndex: 10 }}>
                <Card withBorder shadow="md" radius="md">
                  <Group position="apart" mb="xs">
                    <Title order={5}>Live Interactive Preview</Title>
                    <IconDeviceMobile size={20} color="#ee374d" />
                  </Group>
                  <Text size="xs" c="dimmed" mb="sm">
                    This is an active form replica. You can test inputs,
                    dropdown dependencies, and validation.
                  </Text>
                  <FormPreview
                    title={formConfig.title}
                    description={formConfig.description}
                    fields={formConfig.fields}
                    branches={branches}
                    services={services}
                    formType={formConfig.form_type}
                    branchId={formConfig.branch_id}
                    styles={formStyles}
                  />
                </Card>
              </Box>
            </Grid.Col>

            {/* RIGHT COLUMN: Field Canvas & Optional Fields */}
            <Grid.Col span={12} lg={4}>
              <Stack spacing="md">
                {/* Active Form Canvas */}
                <Card withBorder shadow="sm" radius="md">
                  <BenchHeading
                    title="Form Canvas"
                    subheading="Drag and drop fields to reorder"
                    order={5}
                    fw={700}
                    fz="h4"
                  />
                  <Divider
                    my="sm"
                    label="Active Fields"
                    labelPosition="center"
                    variant="dashed"
                    labelProps={{ color: "dimmed", fw: 500 }}
                  />
                  <CanvasDropZone onDrop={handleDrop}>
                    {formConfig.fields.length === 0 ? (
                      <Box
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "260px",
                          flexDirection: "column",
                        }}
                      >
                        <IconForms size={48} color="#dee2e6" />
                        <Text c="dimmed" mt="md" ta="center">
                          No fields available.
                        </Text>
                      </Box>
                    ) : (
                      <Stack spacing="xs">
                        {formConfig.fields.map((field, index) => (
                          <FieldCard
                            key={field.id}
                            field={field}
                            index={index}
                            onEdit={openEditModal}
                            onRemove={removeField}
                            moveField={moveField}
                          />
                        ))}
                      </Stack>
                    )}
                  </CanvasDropZone>

                  {saveError && (
                    <Alert color="red" mt="md">
                      {saveError}
                    </Alert>
                  )}
                </Card>

                {/* Available Optional Fields Storage */}
                <Card withBorder shadow="sm" radius="md">
                  <Title order={5} mb="xs" sx={{ color: "#1e293b" }}>
                    Available Optional Fields
                  </Title>
                  <Text size="xs" c="dimmed" mb="sm">
                    Fields removed from your form will appear here so you can
                    re-add them at any time.
                  </Text>

                  {removedOptionalFields.length === 0 ? (
                    <Paper p="sm" withBorder sx={{ borderStyle: "dashed" }}>
                      <Text size="xs" c="dimmed" align="center">
                        All optional fields are currently added to your form.
                      </Text>
                    </Paper>
                  ) : (
                    <Stack spacing="xs">
                      {removedOptionalFields.map(([id, field]) => (
                        <Paper
                          key={id}
                          p="xs"
                          withBorder
                          radius="sm"
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Group spacing="xs">
                            {id === "email" ? (
                              <IconMail size={18} color="#ee374d" />
                            ) : (
                              <IconForms size={18} color="#ee374d" />
                            )}
                            <div>
                              <Text size="sm" fw={500}>
                                {field.label}
                              </Text>
                              <Text size="xs" c="dimmed">
                                {field.type}
                              </Text>
                            </div>
                          </Group>
                          <Button
                            size="xs"
                            variant="light"
                            color="red"
                            leftIcon={<IconPlus size={14} />}
                            onClick={() => reAddOptionalField(id)}
                          >
                            Add
                          </Button>
                        </Paper>
                      ))}
                    </Stack>
                  )}
                </Card>
              </Stack>
            </Grid.Col>
          </Grid>
        </Box>

        {/* Edit Field Modal */}
        <EditFieldModal
          field={editingField}
          isOpen={isModalOpen}
          onClose={closeEditModal}
          onSave={updateField}
        />
      </Container>
      <Modal
        opened={isEmbedModalOpen}
        onClose={() => setIsEmbedModalOpen(false)}
        title={<Title order={4}>Share & Embed Code</Title>}
        size="lg"
      >
        {savedFormId && <EmbedSnippet formId={savedFormId} />}
      </Modal>
    </DndProvider>
  );
};

export default FormBuilder;
